import React, {useState, useEffect, createContext, useContext, useCallback} from 'react';
import {Image, Platform, TouchableOpacity, Text, ActivityIndicator, StyleSheet, View} from 'react-native';
import {useSelector} from 'react-redux'
import {NavigationContainer, useNavigation, useRoute, getFocusedRouteNameFromRoute} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';
import {fetchLatestWorkout} from '../reducers/cbcWorkoutSlice';
import DashboardActiveIcon from '../../assets/Brain-Home.png';
import DashboardInactiveIcon from '../../assets/Brain-Home-gray.png';
import SettingsActiveIcon from '../../assets/Gear-settings.png';
import SettingsInactiveIcon from '../../assets/Gear-settings-gray.png';
import {MaterialIcons} from '@expo/vector-icons';
import {navigationRef, navigate} from '../hooks/navigationService';
import * as Linking from 'expo-linking';
import {useQuickAuthentication} from "../hooks/useQuickAuthentication";
import {useHandleDeepLink} from "../hooks/useHandleDeepLink";


/* VIEWS */
import Login from './Login';
import WorkoutDashboard from './dashboard/WorkoutDashboard';
import LoadScreen from './LoadScreen';
import GameView from './GameView';
import ContinueWorkout from './ContinueWorkout';
import WorkoutIntroduction from './WorkoutIntroduction';
import ActivityList from './ActivityList';
import ForgotPassword from './ForgotPassword';
import CbcSettings from './CbcSettings';
import ActivityContainer from './ActivityContainer';
import Reminders from './Reminders';
import Snooze from './Snooze';
import SongList from './SongList';
import WebViewGeneric from './WebViewGeneric';
import DebugPage from "./DebugPage";

import * as Notifications from "expo-notifications";

const Stack = createStackNavigator();
const Tab = createBottomTabNavigator();

const getTabBarVisibility = (route) => {
    const routeName = getFocusedRouteNameFromRoute(route) ?? 'dashboard';
    const hideOnScreens = ['gameview'];  // Add more route names if needed

    // Condition to hide tab bar on 'web' and on specified screens
    return Platform.OS !== 'web' && !hideOnScreens.includes(routeName);
};

function WorkoutDashboardStack({route}) {
    const [dimensions, setDimensions] = useState({width: 0, height: 0});

    const onLayout = (event) => {
        const {width, height} = event.nativeEvent.layout;
        setDimensions({width, height});
    };

    useEffect(() => {
        if (window.parent !== window && dimensions.height >= 150) {
            window.parent.postMessage({
                height: dimensions.height,
            }, '*');
        }
    }, [dimensions]);
    
    
    const {current_workout_activity} = useSelector((state) => state.cbcWorkout);
    const [activityName, setActivityName] = useState('');

    useEffect(() => {
        if (current_workout_activity && current_workout_activity.name) {
            let name = current_workout_activity.name;
            if (name === 'Audio Coherence Training') {
                name = 'Focused Breathing';
            }
            setActivityName(name);
        } else {
            setActivityName('');
        }
    }, [current_workout_activity]);
    
    if (activityName === 'Audio Coherence Training') {
        setActivityName('Focused Breathing');
    }

    const navigation = useNavigation();

    const customBackButton = () => (
        <TouchableOpacity
            style={{flexDirection: 'row', alignItems: 'center'}}
            onPress={() => navigation.navigate('tabnavigator', {screen: 'Training Log', params: {screen: 'dashboard'}})}
        >
            <MaterialIcons name="arrow-back-ios" style={{marginLeft: 10}} size={36} color="#3366ce"/>
            <Text style={{marginLeft: -10, color: "#3366ce", fontSize: 16, fontWeight: 600}}>Back</Text>
        </TouchableOpacity>
    );

    return (
        <Stack.Navigator
            screenOptions={{
                headerShown: true,
                headerBackTitleVisible: true,
                headerTitleAlign: 'center',
                headerLeft: customBackButton,
                headerBackTitle: 'Dashboard'  // Set global back button title here
            }}
            onLayout={onLayout}
        >
            <Stack.Screen
                name='dashboard'
                component={WorkoutDashboard}
                options={{headerLeft: () => null, title: 'Training Log'}}
                onLayout={onLayout}
            />
            <Stack.Screen
                name='gameview'
                component={GameView}
                options={{headerShown: true, title: activityName}}
            />
            <Stack.Screen
                name='continueworkout'
                component={ContinueWorkout}
                options={{title: 'Continue your Workout?'}}
            />
            <Stack.Screen
                name='workoutintro'
                component={WorkoutIntroduction}
                options={{title: ''}}
            />
            <Stack.Screen
                name='activitylist'
                component={ActivityList}
                options={{title: 'Activities'}}
            />
            <Stack.Screen
                name='activitycontainer'
                component={ActivityContainer}
                options={{headerShown: true, title: activityName}}
            />
            <Stack.Screen
                name='songlist'
                component={SongList}
                options={{headerShown: true}}
            />
            <Stack.Screen
                name='webviewgeneric'
                component={WebViewGeneric}
                options={{headerShown: true, title: ''}}
            />
            <Stack.Screen
                name='snooze'
                component={Snooze}
                options={{headerShown: false, title: ''}}
            />
            <Stack.Screen
                name='remindermanager'
                component={Reminders}
                options={{headerShown: true, title: 'Reminder Manager'}}
            />
            <Stack.Screen
                name='debugpage'
                component={DebugPage}
                options={{headerShown: true, title: 'Debug'}}
            />

        </Stack.Navigator>
    );
}

function SettingsStack() {
    return (
        <Stack.Navigator screenOptions={{headerShown: true}}>
            <Stack.Screen
                name='settings'
                component={CbcSettings}
                options={{title: 'Account Settings', headerLeft: () => null, headerShown: true}}
            />
            <Stack.Screen
                name='resetpassword'
                component={ForgotPassword}
                options={{title: 'Reset Password', headerShown: true}}
            />
        </Stack.Navigator>
    );
}

const TabBarHeightContext = createContext();

function MyTabs() {
    const navigation = useNavigation();
    const {tabBarHeight, setTabBarHeight} = useContext(TabBarHeightContext);

    useEffect(() => {
        const unsubscribe = navigation.addListener('state', (e) => {
            const routeName = e.data.state.routes[e.data.state.index].name;
            if (routeName === 'gameview') {
                setTabBarHeight(0);
            } else {
                setTabBarHeight('auto');
            }
        });

        // Return the unsubscribe function to clean up the listener
        return unsubscribe;
    }, [navigation, setTabBarHeight]);

    return (
        <Tab.Navigator
            screenOptions={({route}) => ({
                tabBarIcon: ({focused, color, size}) => {
                    let icon;

                    if (route.name === 'Training Log') {
                        icon = focused ? DashboardActiveIcon : DashboardInactiveIcon;
                    } else if (route.name === 'Settings') {
                        icon = focused ? SettingsActiveIcon : SettingsInactiveIcon;
                    }
                    return <Image source={icon} style={{width: size, height: size, tintColor: color}}/>;
                },
                headerShown: true
            })}
            tabBarOptions={{
                style: {
                    height: tabBarHeight,
                },
            }}
        >
            <Tab.Screen
                name="Training Log"
                component={WorkoutDashboardStack}
                options={({route}) => ({
                    headerShown: false,
                    tabBarStyle: {display: getTabBarVisibility(route) ? 'flex' : 'none'},
                    tabBarVisible: getTabBarVisibility(route),
                })}
            />
            <Tab.Screen
                name="Settings"
                component={SettingsStack}
                options={{headerShown: false}}
            />
        </Tab.Navigator>
    );
}


var linkingSubscription;

function RouterComponent() {

    if (Platform.OS !== "web") {

        const {handleDeepLink} = useHandleDeepLink();


        const linking = {
            prefixes: ['cbc://'],
            config: {
                screens: {
                    loadScreen: 'load',
                    login: 'login',
                    tabnavigator: {
                        screens: {
                            TrainingLog: {
                                screens: {
                                    snooze: 'snooze',
                                    dashboard: 'dashboard',
                                },
                            },
                            Settings: 'settings',
                        },
                    },
                },
            },
        };
        const lastNotificationResponse = Notifications.useLastNotificationResponse();

        useEffect(() => {
            // const notificationListener = Notifications.addNotificationResponseReceivedListener(response => {
            //     const url = response.notification.request.content.data.url;
            //     console.log('notificationListener' , response);
            //     if (url) {
            //         console.log("Notification opened with URL: " + url);
            //         handleDeepLink(url);
            //     }
            // });
            //return () => notificationListener.remove();

            console.log('lastNotificationResponse' , lastNotificationResponse);
            try {
                const url = lastNotificationResponse["notification"]["request"]["content"]["data"]["url"];
                console.log('lastNotificationResponse url', url);
                if (url) {
                    console.log("Notification opened with URL: " + url);
                    handleDeepLink(url);
                }
            } catch (e){
                console.log(e);
            }

        }, [lastNotificationResponse]);

        useEffect(() => {
            console.log('router initial use effect running');
            const getInitialURL = async () => {
                await Linking.getInitialURL().then((url)=>{
                    console.log(url);
                    console.log('initialURL' , url)
                    if (url) {
                        console.log("App was opened with URL: " + url);
                        handleDeepLink(url);
                    }
                });
            };

            const subscription = Linking.addEventListener("url", (event) => {
                console.log('subscription event' , event);
                handleDeepLink(event.url);
            });

            getInitialURL();

            return () => {
                if (subscription) {
                    subscription.remove();
                }
            };
        }, []);
    }

    const [tabBarHeight, setTabBarHeight] = useState('auto');

    return (
        <TabBarHeightContext.Provider value={{tabBarHeight, setTabBarHeight}}>
            <NavigationContainer ref={navigationRef}>
                <Stack.Navigator
                    screenOptions={{
                        headerShown: false
                    }}
                >
                    <Stack.Screen
                        name='loadScreen'
                        component={LoadScreen}
                        options={{headerShown: false}}
                    />
                    <Stack.Screen
                        name='login'
                        component={Login}
                        options={{headerShown: false}}
                    />
                    <Stack.Screen
                        name='tabnavigator'
                        options={{headerShown: false}}
                        children={(props) => <MyTabs {...props} />}
                    />
                </Stack.Navigator>
            </NavigationContainer>
        </TabBarHeightContext.Provider>
    );
}

// const handleDeepLinkObject = (event) => {
//     console.log('handleDeepLinkObject');
//     const url = Linking.createURL(event.url);
//     handleDeepLink(url);
// };
//
// const handleDeepLink = async (url) => {
//     const { authenticate } = useQuickAuthentication();
//
//     console.log(url);
//     const path = Linking.parse(url);
//     console.log(path);
//     const cleanedPath = path.hostname.replace(/\//g, '');
//
//     alert('trying to auth');
//
//     navigate(cleanedPath);
//
// };


export default RouterComponent;